<script setup lang="ts">
import { computed, toRef } from "vue";
import { useAnalysisQuery } from "@/composables/useAnalysisQuery";
import { useViewPopulations } from "@/composables/useViewPopulations";
import type { ChartCard, WordCloudData } from "@/types";
import { POPULATION_COLOR_PALETTES } from "@/lib/chartPalettes";
import { shadeRamp, useAnalysisContext, useCardPopulation } from "../analysisData";
import { WORD_CLOUD_MAX_WORDS, wordCloudColors, wordCloudCountBy } from "../wordCloudPalettes";
import ResultsWordCloudChart, { type CloudWord } from "../Charts/ResultsWordCloudChart.vue";
import AnalysisCardShell from "./AnalysisCardShell.vue";
import PopulationSwitch from "./PopulationSwitch.vue";

const props = defineProps({
  card: { type: Object, required: true },
  readonly: { type: Boolean, default: false },
});

const card = toRef(props, "card");
const { weighted, availableCards } = useAnalysisContext();
const { choices, selected, filters } = useCardPopulation(card);
const { displayedPopulations } = useViewPopulations(card);

const questionId = computed<number | null>(() => props.card.content?.question_id || null);

const excludedWords = computed<string[]>(() =>
  String(props.card.content?.excluded_words || "")
    .split(",")
    .map((word) => word.trim())
    .filter(Boolean),
);

const maxWords = computed(() =>
  Math.min(WORD_CLOUD_MAX_WORDS.max, Math.max(WORD_CLOUD_MAX_WORDS.min, props.card.content?.max_words || WORD_CLOUD_MAX_WORDS.default)),
);

const questionMeta = computed(
  () => (availableCards.value as ChartCard[]).find((c) => (c.questions || []).some((q) => q.id === questionId.value)) || null,
);

const countBy = computed(() => wordCloudCountBy(props.card.content?.count_by, questionMeta.value?.type));

const mainPopulation = computed(() => displayedPopulations.value.find((pop) => pop.token === selected.value) || null);
const comparePopulation = computed(
  () => displayedPopulations.value.find((pop) => pop.token === props.card.content?.compare_population && pop.token !== selected.value) || null,
);
const comparing = computed(() => comparePopulation.value !== null);

function bodyFor(populationFilters: Record<string, unknown>) {
  if (!questionId.value) return null;
  return {
    question_id: questionId.value,
    max_words: comparing.value ? WORD_CLOUD_MAX_WORDS.max : maxWords.value,
    excluded_words: excludedWords.value,
    count_by: countBy.value,
    filters: populationFilters,
  };
}

const body = computed(() => bodyFor(filters.value));
const compareBody = computed(() => (comparePopulation.value ? bodyFor(comparePopulation.value.filters) : null));

const { result, loading, state } = useAnalysisQuery<WordCloudData>("word_cloud", body);
const { result: compareResult, loading: compareLoading } = useAnalysisQuery<WordCloudData>("word_cloud", compareBody);

const respondentsOf = (data: WordCloudData | null) => {
  if (!data) return null;
  return weighted.value ? data.weighted_respondents : data.respondents;
};
const respondents = computed(() => respondentsOf(result.value));

const countsOf = (data: WordCloudData | null) =>
  (data?.words || []).map((word) => ({ text: word.text, count: weighted.value ? word.weighted_nb : word.nb })).filter((word) => word.count > 0);

const words = computed<CloudWord[]>(() =>
  countsOf(result.value)
    .sort((a, b) => b.count - a.count || a.text.localeCompare(b.text))
    .slice(0, maxWords.value),
);

const MIN_LOG_ODDS = 0.4;
const COMPARE_MAX_WORDS = 30;
const SHARED_COLOR = POPULATION_COLOR_PALETTES.slate[400];

const keyOf = (text: string) =>
  text
    .toLowerCase()
    .normalize("NFKD")
    .replace(/\p{Mn}/gu, "")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();

const logOdds = (count: number, base: number) => Math.log((count + 0.5) / (Math.max(base - count, 0) + 0.5));

// Each side keeps its own top words; the ones over-represented against the other side (smoothed
// log-odds of the respondent shares) take the population color, the shared ones stay grey.
function compareWords(own: WordCloudData | null, other: WordCloudData | null, color: string): CloudWord[] {
  const nOwn = Math.max(respondentsOf(own) || 0, 1);
  const nOther = Math.max(respondentsOf(other) || 0, 1);
  const otherCounts = new Map(countsOf(other).map((word) => [keyOf(word.text), word.count]));
  const shades = shadeRamp(color, 3);
  return countsOf(own)
    .sort((a, b) => b.count - a.count || a.text.localeCompare(b.text))
    .slice(0, Math.min(maxWords.value, COMPARE_MAX_WORDS))
    .map((word, idx) => {
      const delta = logOdds(word.count, nOwn) - logOdds(otherCounts.get(keyOf(word.text)) || 0, nOther);
      return { ...word, color: delta > MIN_LOG_ODDS ? shades[idx % shades.length] : SHARED_COLOR };
    });
}

const colors = computed(() => wordCloudColors(props.card.content?.palette, props.card.content?.colors));

const compareSides = computed(() => {
  if (!mainPopulation.value || !comparePopulation.value || !result.value || !compareResult.value) return [];
  return [
    { population: mainPopulation.value, own: result.value, other: compareResult.value },
    { population: comparePopulation.value, own: compareResult.value, other: result.value },
  ].map(({ population, own, other }) => ({
    key: population.token,
    label: population.label,
    color: population.color,
    respondents: respondentsOf(own) || 0,
    words: compareWords(own, other, population.color),
  }));
});

const cardState = computed(() => state(comparing.value ? compareSides.value.some((side) => side.words.length > 0) : words.value.length > 0));
</script>

<template>
  <AnalysisCardShell
    :card="card"
    :readonly="readonly"
    :respondents="respondents"
    :weighted="weighted"
    :respondents-hint="`Respondents who answered the question. ${countBy === 'answer' ? 'An answer' : 'A word'} counts once per respondent.`"
    description="The answers or words respondents give most in an open question, sized by how many respondents wrote them. Case, accents and small typos are grouped; common connectors of the survey's languages are left out. Compared with a second population, each side shows its own top words, colored when they are more typical of that side and grey when both share them."
    :state="cardState"
    state-message="Select an open question to build the word cloud.">
    <template #subtitle>
      <p v-if="questionMeta" class="text-xs text-muted-foreground">
        {{ questionMeta.name }}
        <template v-if="comparing">· {{ mainPopulation?.label }} vs {{ comparePopulation?.label }}</template>
      </p>
    </template>
    <template #toolbar>
      <PopulationSwitch v-if="choices.length > 1" :card="card" :populations="choices" :selected="selected" />
    </template>

    <div v-if="comparing" :class="{ 'opacity-50': loading || compareLoading }">
      <div class="grid grid-cols-2 gap-4">
        <div v-for="side in compareSides" :key="side.key" class="min-w-0 space-y-1">
          <p class="text-xs font-semibold" :style="{ color: side.color }">{{ side.label }} <span class="font-normal text-muted-foreground">n={{ side.respondents }}</span></p>
          <ResultsWordCloudChart
            :words="side.words"
            :colors="[]"
            :respondents="side.respondents"
            :contrast="card.content?.size_contrast"
            :layout="card.content?.layout" />
        </div>
      </div>
      <div class="mt-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-2xs text-muted-foreground">
        <span v-for="side in compareSides" :key="side.key" class="flex items-center gap-1.5">
          <span class="h-2 w-2 rounded-full" :style="{ backgroundColor: side.color }" />More typical of {{ side.label }}
        </span>
        <span class="flex items-center gap-1.5"><span class="h-2 w-2 rounded-full" :style="{ backgroundColor: SHARED_COLOR }" />Shared</span>
      </div>
    </div>
    <div v-else :class="{ 'opacity-50': loading }">
      <ResultsWordCloudChart
        :words="words"
        :colors="colors"
        :respondents="respondents || 0"
        :contrast="card.content?.size_contrast"
        :layout="card.content?.layout" />
    </div>
  </AnalysisCardShell>
</template>
